import axios, { Method } from 'axios';
import { supplant } from '@utils/string.utils';

interface GraphqlAPI {
    name: string;
    query: string;
}

type GraphqlAPIS = Record<string, GraphqlAPI>;

export function createGraphqlFetcher<T extends GraphqlAPIS>(
    apis: T
): Record<
    keyof T,
    <Data = any, Variables = any>(variables?: Variables, otherParams?: any, headerParams?: any) => Promise<Data>
> {
    return Object.fromEntries(
        Object.entries(apis).map(([apiKey, apiConfig]: [keyof T, GraphqlAPI]) => [
            apiKey,
            <Data = any, Variables = any>(variables?: Variables, otherParams?: any, headerParams?: any) =>
                axios
                    .request<Data>({
                        headers: { 'Access-Control-Allow-Origin': '*', ...(typeof headerParams === 'object' ? headerParams : {}) },
                        url: `${import.meta.env.VITE_EXPERIENCE_SERVER_URL}/graphql`,
                        method: 'POST',
                        data: {
                            operationName: apiConfig.name,
                            query: apiConfig.query,
                            variables
                        },
                        ...(typeof otherParams === 'object' ? otherParams : {})
                    })
                    .then((response: { data: any }) => {
                        const { code, message, status } = response.data || {};
                        if (status && status.toLowerCase() === 'error') {
                            return Promise.reject({
                                code,
                                message,
                                status
                            });
                        }

                        if (response?.data?.data) {
                            const realData = Object.values(response?.data?.data);
                            if (realData?.length) return Promise.resolve(realData[0]);
                        }
                        return Promise.resolve((response?.data ?? response) as Data);
                    })
                    .catch((error: any) => {
                        if (axios.isCancel(error)) return Promise.reject(error);
                        if (error?.response?.data) return Promise.reject(error.response.data);
                        return Promise.reject(error);
                    }) as Promise<Data>
        ])
    ) as any;
}

interface RestfulAPI {
    url: string;
    method: Method;
    supplant?: true;
}

type RestfulAPIS = Record<string, RestfulAPI>;

export function createFetcher<T extends RestfulAPIS>(
    apis: T
): Record<keyof T, <Data = any, Variables = any>(variables?: Variables, extraConfig?: any) => Promise<Data>> {
    return Object.fromEntries(
        Object.entries(apis).map(([apiKey, apiConfig]: [keyof T, RestfulAPI]) => [
            apiKey,
            <Data = any, Variables = any>(variables?: Variables, extraConfig?: any) => 
                axios
                    .request<Data>({
                        headers: { 'Access-Control-Allow-Origin': '*', ...(extraConfig && extraConfig.headers) },
                        url: apiConfig.supplant
                            ? supplant(apiConfig.url, variables)
                            : apiConfig.url,
                        method: apiConfig.method,
                        ...(apiConfig.supplant ? {} : { data: variables })
                    })
                    .then((response: any) => {
                        return Promise.resolve((response?.data ?? response) as Data);
                    })
                    .catch((error: any) => {
                        if (axios.isCancel(error)) return Promise.reject(error);
                        if (error?.response?.data) return Promise.reject(error.response.data);
                        return Promise.reject(error);
                    }) as Promise<Data>
            
        ])
    ) as any;
}
